import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiPlay,
  mdiAutoFix,
  mdiLoading,
  mdiViewDashboardOutline,
  mdiFilePdfBox,
  mdiEmailOutline,
  mdiExport,
  mdiImport,
  mdiDeleteOutline,
  mdiUndo,
  mdiRedo,
  mdiShareVariantOutline,
  mdiCogOutline,
  mdiChevronRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiAlertCircleOutline,
  mdiRobotOutline,
} from '@mdi/js';
import html2canvas from 'html2canvas';
import { ElementsLibrary } from './components/ElementsLibrary';
import { FlowCanvas } from './components/FlowCanvas';
import { PropertiesPanel } from './components/PropertiesPanel';
import { findCircuits } from './services/ports';
import { FluidNamesContext, defaultFluidId, setCircuitCoolant, useFluids, withPumpCoolants } from './services/coolant';
import { ScenariosPanel } from './components/ScenariosPanel';
import { SimulationResults } from './components/SimulationResults';
import { RecommendationTable } from './components/RecommendationTable';
import { TemplatePickerModal } from './components/TemplatePickerModal';
import { RfqModal } from './components/RfqModal';
import { ShareDialog } from './components/ShareDialog';
import { ValidationErrors } from './components/ValidationErrors';
import { AgentPanel } from './components/AgentPanel';
import { AgentStatus } from './services/apiService';
import { WelcomeScreen } from './components/WelcomeScreen';
import { CircuitSchema, PanelState, ComponentType, ComponentTypeEnum, CircuitTemplate, OperatingScenario, RecommendationResult } from './types';
import { apiService } from './services/apiService';
import { useSimulation } from './hooks/useSimulation';
import { useHistory } from './hooks/useHistory';
import { useAuthProvider, AuthContext } from './hooks/useAuth';
import { serializeToXml, deserializeFromXml } from './services/xmlSerializer';
import { createEdgeId, createNodeId } from './services/ids';
import {
  emptySimulationParams,
  normalizeSchema,
  normalizeSimulationParams,
} from './services/schemaNormalizer';
import { useUnitSystem, UnitContext } from './units';
import { AuthModal } from './components/AuthModal';
import { UserMenu } from './components/UserMenu';
import { CircuitBrowser } from './components/CircuitBrowser';
import { DropdownMenu, DropdownMenuItem } from './components/DropdownMenu';
import { LoginScreen } from './components/LoginScreen';

/* ────────────────────────── Layout ────────────────────────── */

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: 'Sora', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const LeftPanel = styled.div<{ collapsed: boolean }>`
  width: ${props => props.collapsed ? '40px' : '260px'};
  background-color: #ffffff;
  border-right: 1px solid #e8eaed;
  transition: width 0.2s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CenterPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  overflow: hidden;
`;

const CanvasArea = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const RightPanel = styled.div<{ visible: boolean }>`
  width: 300px;
  background-color: #ffffff;
  border-left: 1px solid #e8eaed;
  transition: transform 0.2s ease;
  transform: ${props => props.visible ? 'translateX(0)' : 'translateX(100%)'};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/* ────────────────────────── Top Bar ────────────────────────── */

const TopBarContainer = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 14px;
  background: #202D61;
  border-bottom: none;
  flex-shrink: 0;
  gap: 4px;
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const LogoImg = styled.img`
  height: 24px;
  width: auto;
  filter: brightness(0) invert(1);
`;

const LogoFallback = styled.span`
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #ffffff;
`;

const LogoSubtitle = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 6px;
  flex-shrink: 0;
`;

const TbButton = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid ${props => props.primary ? '#6d8dbf' : 'rgba(255,255,255,0.25)'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Sora', sans-serif;
  background: ${props => props.primary ? '#6d8dbf' : 'transparent'};
  color: #ffffff;
  white-space: nowrap;

  &:hover {
    background: ${props => props.primary ? '#5a7aab' : 'rgba(255,255,255,0.1)'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SpinIcon = styled(Icon)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const TopBarSpacer = styled.div`
  flex: 1;
`;

const UnitToggle = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ $active }) => $active ? '#1a73e8' : '#5f6368'};
  background: ${({ $active }) => $active ? '#e8f0fe' : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? '#1a73e8' : '#dadce0'};
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.3px;

  &:hover {
    background: ${({ $active }) => $active ? '#d2e3fc' : '#f1f3f4'};
  }
`;

/* ────────────────────────── Right panel pieces ────────────────────────── */

const PanelHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid #e8eaed;
  background-color: #fafbfc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 12px;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  color: #9aa0a6;
  font-size: 12px;

  &:hover {
    background-color: #e8eaed;
    color: #5f6368;
  }
`;

const ErrorBanner = styled.div`
  padding: 6px 12px;
  background: #fce8e6;
  color: #c5221f;
  font-size: 12px;
  border-bottom: 1px solid #f5c6cb;
  flex-shrink: 0;
`;

const RightPanelSection = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e8eaed;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9aa0a6;
  padding: 0;

  &:hover {
    background-color: #e8eaed;
    color: #5f6368;
  }
`;

/* ────────────────────────── Bottom Panel ────────────────────────── */

const BottomPanel = styled.div<{ collapsed: boolean; $height: number }>`
  background: #ffffff;
  border-top: 1px solid #e8eaed;
  flex-shrink: 0;
  height: ${props => props.collapsed ? '36px' : `${props.$height}px`};
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  z-index: 2;

  &:hover, &:active {
    background: rgba(26, 115, 232, 0.3);
  }
`;

const BottomPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 36px;
  border-bottom: 1px solid #e8eaed;
  background: #fafbfc;
  flex-shrink: 0;
`;

const BottomTabBar = styled.div`
  display: flex;
  gap: 0;
  align-items: stretch;
  height: 100%;
`;

const BottomTab = styled.button<{ active: boolean; disabled?: boolean }>`
  padding: 0 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#1a73e8' : 'transparent'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 11px;
  font-weight: ${props => props.active ? 600 : 400};
  color: ${props => props.disabled ? '#bdc1c6' : props.active ? '#1a73e8' : '#5f6368'};
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;

  &:hover {
    background: ${props => props.disabled ? 'transparent' : props.active ? 'transparent' : '#f1f3f4'};
  }
`;

const BottomPanelToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  color: #9aa0a6;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #e8eaed;
    color: #5f6368;
  }
`;

const BottomPanelContent = styled.div`
  flex: 1;
  overflow: auto;
`;

const ThermalWarningBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #fef7e0;
  border-top: 1px solid #f5c6cb;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: #e37400;
`;

const ThermalWarningText = styled.span`
  flex: 1;
`;

const CtaBtn = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  border: ${p => p.$primary ? 'none' : '1px solid #dadce0'};
  background: ${p => p.$primary ? '#202D61' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#5f6368'};

  &:hover {
    background: ${p => p.$primary ? '#1a2450' : '#f1f3f4'};
  }
`;


const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  font-size: 12px;
  flex-shrink: 0;
`;

const ReadOnlyBanner = styled(InfoBanner)`
  background: #e8f0fe;
  color: #1a73e8;
  border-bottom: 1px solid #d2e3fc;
`;

const SharedCircuitBanner = styled(InfoBanner)`
  background: #e6f4ea;
  color: #137333;
  border-bottom: 1px solid #ceead6;
`;

const BannerLink = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  font-family: inherit;
`;

const SaveBtn = styled.button`
  padding: 4px 12px;
  background: #137333;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  &:hover { background: #0d5c29; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: #f8f9fa;
  font-family: 'Sora', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #5f6368;
  font-size: 14px;
`;

const AnnotationSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
`;

const ColorSwatchGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ColorSwatch = styled.button<{ $color: string; $active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 2px solid ${props => props.$active ? '#1a73e8' : 'transparent'};
  background: ${props => props.$color};
  cursor: pointer;
  outline: none;
  box-shadow: ${props => props.$active ? '0 0 0 1px #1a73e8' : 'inset 0 0 0 1px rgba(0,0,0,0.1)'};
  transition: transform 0.1s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.15);
  }
`;

const ANNOTATION_COLORS = [
  { value: '#ea4335', label: 'Red' },
  { value: '#1a73e8', label: 'Blue' },
  { value: '#34a853', label: 'Green' },
  { value: '#f9ab00', label: 'Orange' },
  { value: '#9334e6', label: 'Purple' },
  { value: '#00bcd4', label: 'Teal' },
  { value: '#795548', label: 'Brown' },
  { value: '#9aa0a6', label: 'Gray' },
];

/* ────────────────────────── App ────────────────────────── */

const App: React.FC = () => {
  const [, , unitCtx] = useUnitSystem();
  const auth = useAuthProvider();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login'>('login');
  const [showCircuitBrowser, setShowCircuitBrowser] = useState(false);
  const [sharedCircuitContext, setSharedCircuitContext] = useState<{
    shareId: string;
    title: string;
  } | null>(null);
  const [savingShared, setSavingShared] = useState(false);

  const [schema, setSchema] = useState<CircuitSchema>(() => {
    const saved = localStorage.getItem('circuit-schema');
    if (saved) {
      try {
        return normalizeSchema(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load saved schema:', error);
      }
    }
    return {
      nodes: [],
      edges: [],
      annotations: [],
      simulation_params: emptySimulationParams(),
    };
  });

  const [panels, setPanels] = useState<PanelState>({
    library: true,
    properties: false,
  });

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedElements, setSelectedElements] = useState<{ nodes: string[]; edges: string[] }>({
    nodes: [],
    edges: [],
  });
  const [components, setComponents] = useState<ComponentType[]>([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showRfqModal, setShowRfqModal] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const [showWelcome, setShowWelcome] = useState(() => {
    const saved = localStorage.getItem('circuit-schema');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return !(parsed.nodes && parsed.nodes.length > 0);
      } catch {
        return true;
      }
    }
    return true;
  });
  const [templates, setTemplates] = useState<CircuitTemplate[]>([]);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [annotationRequest, setAnnotationRequest] = useState<{ type: 'label' | 'comment'; id: number } | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [clipboard, setClipboard] = useState<{ nodes: any[]; edges: any[] } | null>(null);
  const [openPickerNodeId, setOpenPickerNodeId] = useState<string | null>(null);
  const [bottomTab, setBottomTab] = useState<'scenarios' | 'results'>('scenarios');
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const [bottomHeight, setBottomHeight] = useState(260);

  const importInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [logoError, setLogoError] = useState(false);

  const isReadOnly = !auth.user && sharedCircuitContext !== null;

  const simulation = useSimulation();
  const history = useHistory();

  const [showAgent, setShowAgent] = useState(false);
  const [fitViewRequest, setFitViewRequest] = useState(0);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);

  // Whether the circuit assistant is configured (and for whom).
  useEffect(() => {
    apiService.getAgentStatus()
      .then(setAgentStatus)
      .catch(() => setAgentStatus({ enabled: false, configured: false, requires_login: false, model: null }));
  }, [auth.user]);

  // Load components from API
  useEffect(() => {
    const loadComponents = async () => {
      try {
        const data = await apiService.getComponents();
        setComponents(data);
      } catch (error) {
        console.error('Failed to load components:', error);
      }
    };
    loadComponents();
  }, []);

  // Load templates from API
  useEffect(() => {
    apiService.getTemplates()
      .then(setTemplates)
      .catch(() => {}); // Non-critical
  }, []);

  // Auto-save schema to localStorage
  useEffect(() => {
    localStorage.setItem('circuit-schema', JSON.stringify(schema));
  }, [schema]);

  // Push state to history for undo/redo. An automatic follow-up edit (a new
  // pump getting its default coolant) is folded into the step that caused it,
  // so undo never lands on the intermediate state and gets bounced back.
  const amendHistoryRef = useRef(false);
  useEffect(() => {
    if (amendHistoryRef.current) {
      amendHistoryRef.current = false;
      history.replaceState(schema);
    } else {
      history.pushState(schema);
    }
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  // The coolant is chosen on each pump. A pump without one — just dropped,
  // pasted from a template, built by the assistant — starts with the default.
  const { fluids } = useFluids();
  const fluidNames = useMemo(
    () => Object.fromEntries(fluids.map(f => [f.id, f.name])) as Record<number, string>,
    [fluids],
  );
  useEffect(() => {
    if (fluids.length === 0) return;
    const filled = withPumpCoolants(schema, defaultFluidId(fluids, schema.simulation_params?.fluid_id));
    if (filled) {
      amendHistoryRef.current = true;
      setSchema(filled);
    }
  }, [schema, fluids]);

  /** Admin: publish a canvas component to the library and link the node to it. */
  const handleAddToLibrary = useCallback(async (nodeId: string, meta: { name: string; manufacturer?: string; description?: string }) => {
    const node = schema.nodes.find(n => n.id === nodeId);
    if (!node) return;
    const created = await apiService.addComponentFromCanvas(node, meta);
    setComponents(prev => [...prev, created]);
    setSchema(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => {
        if (n.id !== nodeId) return n;
        // The node now uses the library model; its coolant stays with this canvas.
        const properties: Record<string, any> = { ...created.default_properties };
        if (n.data.properties?.fluid_id) properties.fluid_id = n.data.properties.fluid_id;
        return {
          ...n,
          data: { ...n.data, component: created, properties, custom_name: n.data.custom_name || created.name },
        };
      }),
    }));
  }, [schema.nodes]);

  const handleCircuitCoolantChange = useCallback((nodeId: string, fluidId: number) => {
    setSchema(prev => setCircuitCoolant(prev, nodeId, fluidId));
  }, []);

  // Load shared circuit from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const circuitId = params.get('circuit');
    if (circuitId) {
      apiService.loadSharedCircuit(circuitId).then(data => {
        if (data.circuit_schema) {
          setSchema(normalizeSchema(data.circuit_schema));
          setShowWelcome(false);
          setSharedCircuitContext({
            shareId: circuitId,
            title: data.title || 'Shared Circuit',
          });
        }
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      }).catch(err => {
        console.error('Failed to load shared circuit:', err);
      });
    }
  }, []);

  const handleUndo = useCallback(() => {
    const prev = history.undo();
    if (prev) {
      setSchema(s => ({
        ...prev,
        simulation_params: s.simulation_params,
      }));
    }
  }, [history]);

  const handleRedo = useCallback(() => {
    const next = history.redo();
    if (next) {
      setSchema(s => ({
        ...next,
        simulation_params: s.simulation_params,
      }));
    }
  }, [history]);

  const handleDeleteSelected = useCallback(() => {
    const count = selectedElements.nodes.length + selectedElements.edges.length;
    if (count === 0) return;

    if (window.confirm(`Delete ${count} selected element${count > 1 ? 's' : ''}?`)) {
      setSchema(prev => ({
        ...prev,
        nodes: prev.nodes.filter(node => !selectedElements.nodes.includes(node.id)),
        edges: prev.edges.filter(edge => !selectedElements.edges.includes(edge.id)),
      }));
      setSelectedNodeId(null);
      setSelectedElements({ nodes: [], edges: [] });
    }
  }, [selectedElements]);

  // Copy selected nodes
  const handleCopy = useCallback(() => {
    if (selectedElements.nodes.length === 0) return;
    const nodesToCopy = schema.nodes.filter(n => selectedElements.nodes.includes(n.id));
    const edgesToCopy = schema.edges.filter(e =>
      selectedElements.nodes.includes(e.source) && selectedElements.nodes.includes(e.target)
    );
    setClipboard({ nodes: nodesToCopy, edges: edgesToCopy });
  }, [selectedElements, schema]);

  // Paste clipboard nodes
  const handlePaste = useCallback(() => {
    if (!clipboard || clipboard.nodes.length === 0) return;
    const offset = 40;
    const idMap: Record<string, string> = {};

    const newNodes = clipboard.nodes.map(n => {
      const newId = createNodeId();
      idMap[n.id] = newId;
      return {
        ...n,
        id: newId,
        position: { x: n.position.x + offset, y: n.position.y + offset },
        selected: true,
      };
    });

    const newEdges = clipboard.edges
      .filter(e => idMap[e.source] && idMap[e.target])
      .map(e => ({
        ...e,
        id: createEdgeId(),
        source: idMap[e.source],
        target: idMap[e.target],
        selected: false,
      }));

    setSchema(prev => ({
      ...prev,
      nodes: [...prev.nodes.map(n => ({ ...n, selected: false })), ...newNodes],
      edges: [...prev.edges, ...newEdges],
    }));
  }, [clipboard]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input
      const tag = (event.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Disable editing shortcuts in read-only mode
      if (isReadOnly) return;

      if (event.key === 'Delete') {
        event.preventDefault();
        handleDeleteSelected();
      }
      // Undo: Ctrl+Z
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Shift+Z or Ctrl+Y
      if ((event.ctrlKey || event.metaKey) && (event.key === 'Z' || event.key === 'y') && (event.shiftKey || event.key === 'y')) {
        event.preventDefault();
        handleRedo();
      }
      // Copy: Ctrl+C
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        handleCopy();
      }
      // Paste: Ctrl+V
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        handlePaste();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleDeleteSelected, handleUndo, handleRedo, handleCopy, handlePaste, isReadOnly]);

  const handleNodeSelect = (nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    setSelectedElements(prev => ({
      ...prev,
      nodes: nodeId ? [nodeId] : [],
      edges: nodeId ? [] : prev.edges,
    }));
    setPanels(prev => ({ ...prev, properties: nodeId !== null }));
  };

  const handleElementSelect = (type: 'node' | 'edge', id: string | null) => {
    if (type === 'node') {
      setSelectedNodeId(id);
      setPanels(prev => ({ ...prev, properties: id !== null }));
    } else {
      setSelectedNodeId(null);
      setPanels(prev => ({ ...prev, properties: false }));
    }

    setSelectedElements(() => {
      if (type === 'node') {
        return { nodes: id ? [id] : [], edges: [] };
      }
      return { nodes: [], edges: id ? [id] : [] };
    });
  };

  const handleMultiSelect = useCallback((nodeIds: string[], edgeIds: string[]) => {
    setSelectedElements({ nodes: nodeIds, edges: edgeIds });
    setSelectedNodeId(nodeIds.length === 1 ? nodeIds[0] : null);
    setPanels(prev => ({ ...prev, properties: nodeIds.length === 1 }));
  }, []);

  const handleNodeDoubleClick = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedElements({ nodes: [nodeId], edges: [] });
    setPanels(prev => ({ ...prev, properties: true }));
    setOpenPickerNodeId(nodeId);
  }, []);

  const handlePickerOpened = useCallback(() => {
    setOpenPickerNodeId(null);
  }, []);

  const togglePanel = (panelType: keyof PanelState) => {
    setPanels(prev => ({ ...prev, [panelType]: !prev[panelType] }));
  };

  const handleControlPointChange = (edgeId: string, controlPoints: { x: number; y: number }[]) => {
    setSchema(prev => ({
      ...prev,
      edges: prev.edges.map(edge =>
        edge.id === edgeId
          ? { ...edge, data: { ...edge.data, controlPoints } }
          : edge
      ),
    }));
  };

  const handleScenariosChange = useCallback((scenarios: OperatingScenario[]) => {
    setSchema(prev => ({
      ...prev,
      simulation_params: {
        ...normalizeSimulationParams(prev.simulation_params),
        scenarios,
      },
    }));
  }, []);

  const handleSimulate = useCallback(async () => {
    // Validate before simulating
    setValidationErrors([]);
    try {
      const validation = await apiService.validate(schema);
      if (!validation.valid) {
        setValidationErrors(validation.errors);
        return;
      }
    } catch {
      // If validation endpoint fails, proceed with simulation anyway
    }
    simulation.simulate(schema);
  }, [simulation, schema]);

  /** Accept a circuit proposed by the assistant (one undo step). */
  const handleAgentApply = useCallback((proposed: CircuitSchema, opts: { simulate: boolean }) => {
    const next = normalizeSchema({ ...proposed, viewport: schema.viewport });
    setSchema(next);
    setValidationErrors([]);
    setShowWelcome(false);
    simulation.clearResults();
    setFitViewRequest(n => n + 1);
    if (opts.simulate) {
      // The assistant simulated its proposal; show the same numbers for the applied canvas.
      simulation.simulate(next);
      setBottomTab('results');
      setBottomCollapsed(false);
    }
  }, [schema.viewport, simulation]);

  const handleRecommend = useCallback(async () => {
    const hasTemperatureLimit = schema.nodes.some(n => {
      const mt = n.data.max_temperatures;
      return mt && (mt.inlet != null || mt.outlet != null);
    });
    if (!hasTemperatureLimit) {
      alert('Recommend Cooling requires at least one component with a max inlet or max outlet temperature defined.');
      return;
    }
    await simulation.recommend(schema);
    setShowRecommendations(true);
  }, [simulation, schema]);

  const handleRecommendationSelect = useCallback((rec: RecommendationResult) => {
    if (rec.results.length > 0) {
      // Load this recommendation's simulation results
    }
    setShowRecommendations(false);
  }, []);

  const handleTemplateSelect = useCallback((templateSchema: CircuitSchema) => {
    setSchema(normalizeSchema(templateSchema));
    setShowTemplatePicker(false);
    setShowWelcome(false);
    simulation.clearResults();
    apiService.trackEvent('template_loaded');
  }, [simulation]);

  const handleWelcomeTemplateSelect = useCallback(async (templateId: number) => {
    try {
      const template = await apiService.getTemplate(templateId);
      if (template.schema) {
        handleTemplateSelect(template.schema);
      }
    } catch {
      // Fallback: just dismiss welcome
      setShowWelcome(false);
    }
  }, [handleTemplateSelect]);

  const handleWelcomeStartBlank = useCallback(() => {
    setShowWelcome(false);
  }, []);

  const handleWelcomeImport = useCallback(() => {
    setShowWelcome(false);
    importInputRef.current?.click();
  }, []);


  const handleExportPdf = useCallback(async () => {
    setPdfExporting(true);
    try {
      // Capture canvas screenshot
      let circuitImage: string | undefined;
      const canvasEl = canvasRef.current?.querySelector('.react-flow') as HTMLElement | null;
      if (canvasEl) {
        try {
          // Hide UI overlays before capture
          const overlays = canvasEl.querySelectorAll<HTMLElement>(
            '.react-flow__minimap, .react-flow__controls, .react-flow__panel, .react-flow__attribution'
          );
          overlays.forEach(el => el.style.display = 'none');

          const canvas = await html2canvas(canvasEl, {
            backgroundColor: '#f8f9fa',
            scale: 2,
            useCORS: true,
            logging: false,
          });
          circuitImage = canvas.toDataURL('image/png');

          // Restore overlays
          overlays.forEach(el => el.style.display = '');
        } catch {
          // Non-critical — PDF will generate without image
        }
      }

      const blob = await apiService.exportPdf({
        schema,
        simulation_results: simulation.results.length > 0 ? simulation.results : undefined,
        circuit_image: circuitImage,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bspl_simulation_report.pdf';
      link.click();
      URL.revokeObjectURL(url);
      apiService.trackEvent('pdf_exported');
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setPdfExporting(false);
    }
  }, [schema, simulation.results]);

  // ── File operations (moved from FlowCanvas) ──

  const handleExport = useCallback(() => {
    const xmlStr = serializeToXml(schema);
    const dataUri = 'data:application/xml;charset=utf-8,' + encodeURIComponent(xmlStr);
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', 'circuit-schema.xml');
    link.click();
  }, [schema]);

  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        let imported: CircuitSchema;

        if (file.name.endsWith('.xml') || content.trimStart().startsWith('<?xml') || content.trimStart().startsWith('<circuit-schema')) {
          imported = deserializeFromXml(content);
        } else {
          imported = JSON.parse(content);
        }

        setSchema(normalizeSchema(imported));
        setShowWelcome(false);
      } catch (error) {
        console.error('Failed to import schema:', error);
        alert('Invalid file format. Please use .xml or .json files.');
      }
    };
    reader.readAsText(file);

    // Reset the input so re-importing the same file triggers onChange
    event.target.value = '';
  }, []);

  const handleClear = useCallback(() => {
    if (window.confirm('Clear all elements from the canvas?')) {
      setSchema({
        nodes: [],
        edges: [],
        annotations: [],
        simulation_params: schema.simulation_params,
      });
    }
  }, [schema.simulation_params]);

  // ── Bottom panel resize ──

  const handleBottomResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = bottomHeight;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = startY - ev.clientY;
      const newHeight = Math.max(120, Math.min(startHeight + delta, window.innerHeight * 0.7));
      setBottomHeight(newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [bottomHeight]);

  // ── Annotation trigger ──

  const handleAddAnnotation = useCallback((type: 'label' | 'comment') => {
    setAnnotationRequest({ type, id: Date.now() });
  }, []);

  // ── Derived state ──

  const selectedNode = selectedNodeId
    ? schema.nodes.find(node => node.id === selectedNodeId && node.type !== 'annotationNode')
    : null;

  // Which circuit the selected component belongs to (the coolant is per circuit).
  const circuits = useMemo(() => findCircuits(schema.nodes, schema.edges), [schema.nodes, schema.edges]);
  const selectedCircuitInfo = useMemo(() => {
    if (!selectedNode) return null;
    const circuit = circuits.find(c => c.nodeIds.includes(selectedNode.id));
    return circuit ? { index: circuit.index, total: circuits.length, size: circuit.nodeIds.length } : null;
  }, [circuits, selectedNode]);

  const selectedAnnotation = selectedNodeId
    ? schema.nodes.find(node => node.id === selectedNodeId && node.type === 'annotationNode')
    : null;

  const handleAnnotationColorChange = useCallback((color: string) => {
    if (!selectedNodeId) return;
    setSchema(prev => ({
      ...prev,
      nodes: prev.nodes.map(n =>
        n.id === selectedNodeId ? { ...n, data: { ...n.data, color } } : n
      ),
    }));
  }, [selectedNodeId]);

  const nodeNames = useMemo(() => {
    const map: Record<string, string> = {};
    schema.nodes.forEach(n => {
      if (n.data.component) {
        map[n.id] = n.data.custom_name || n.data.component.name;
      }
    });
    return map;
  }, [schema.nodes]);

  const nodeThresholds = useMemo(() => {
    const map: Record<string, { inlet?: number; outlet?: number }> = {};
    schema.nodes.forEach(n => {
      if (n.data.max_temperatures) {
        map[n.id] = n.data.max_temperatures;
      }
    });
    return map;
  }, [schema.nodes]);

  const nodeComponentTypes = useMemo(() => {
    const map: Record<string, ComponentTypeEnum> = {};
    schema.nodes.forEach(n => {
      if (n.data.component) {
        map[n.id] = n.data.component.component_type;
      }
    });
    return map;
  }, [schema.nodes]);

  // Detect thermal limit violations across all simulation results
  const thermalWarning = useMemo(() => {
    if (simulation.results.length === 0) return null;
    for (const result of simulation.results) {
      for (const [nodeId, comp] of Object.entries(result.components)) {
        const thresholds = nodeThresholds[nodeId];
        if (!thresholds) continue;
        if (thresholds.inlet != null && comp.inlet_temperature > thresholds.inlet) {
          return { name: nodeNames[nodeId] || nodeId, scenario: result.scenario_name || result.scenario_id };
        }
        if (thresholds.outlet != null && comp.outlet_temperature > thresholds.outlet) {
          return { name: nodeNames[nodeId] || nodeId, scenario: result.scenario_name || result.scenario_id };
        }
      }
    }
    return null;
  }, [simulation.results, nodeThresholds, nodeNames]);

  // Auto-switch to Results tab when simulation completes
  useEffect(() => {
    if (simulation.results.length > 0) {
      setBottomTab('results');
      setBottomCollapsed(false);
    }
  }, [simulation.results]);

  const simParams = useMemo(
    () => normalizeSimulationParams(schema.simulation_params),
    [schema.simulation_params],
  );
  const canSimulate = simParams.scenarios.length > 0 && schema.nodes.length > 0;

  // Filter available models for the selected node's component type
  const availableModels = useMemo(() => {
    if (!selectedNode) return [];
    return components.filter(c => c.component_type === selectedNode.data.component.component_type);
  }, [components, selectedNode]);

  const openAuthModal = useCallback((mode: 'login') => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  }, []);

  const handleSaveSharedCircuit = useCallback(async () => {
    if (!sharedCircuitContext) return;
    setSavingShared(true);
    try {
      await apiService.saveCircuit({
        title: sharedCircuitContext.title,
        circuit_schema: schema,
      });
      setSharedCircuitContext(null);
    } catch (err) {
      console.error('Failed to save shared circuit:', err);
      alert('Failed to save circuit. Please try again.');
    }
    setSavingShared(false);
  }, [sharedCircuitContext, schema]);

  const manageMenuItems: DropdownMenuItem[] = useMemo(() => [
    { label: 'Export', icon: mdiExport, onClick: handleExport },
    { label: 'Import', icon: mdiImport, onClick: () => importInputRef.current?.click() },
    { label: '', divider: true },
    { label: 'My Circuits', icon: mdiCogOutline, onClick: () => setShowCircuitBrowser(true), disabled: !auth.user },
    { label: '', divider: true },
    { label: 'Clear Canvas', icon: mdiDeleteOutline, onClick: handleClear },
  ], [handleExport, handleClear, auth.user]);

  // Auth gate: loading spinner
  if (auth.loading) {
    return (
      <AuthContext.Provider value={auth}>
        <LoadingScreen>Loading...</LoadingScreen>
      </AuthContext.Provider>
    );
  }

  // Auth gate: login screen (no user, no shared circuit)
  if (!auth.user && !sharedCircuitContext) {
    return (
      <AuthContext.Provider value={auth}>
        <LoginScreen onLogin={() => {}} />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
    <UnitContext.Provider value={unitCtx}>
    <FluidNamesContext.Provider value={fluidNames}>
    <AppContainer>
      {/* ── Top Bar ── */}
      <TopBarContainer>
        <LogoArea>
          {!logoError ? (
            <LogoImg
              src="/bspl_logo.svg"
              alt="BSPL"
              onError={() => setLogoError(true)}
            />
          ) : (
            <LogoFallback>BSPL</LogoFallback>
          )}
          <LogoSubtitle>Cooling Circuit Designer</LogoSubtitle>
        </LogoArea>

        <Divider />

        {!isReadOnly && (
          <>
            {/* Undo/Redo */}
            <TbButton onClick={handleUndo} disabled={!history.canUndo} title="Undo (Ctrl+Z)">
              <Icon path={mdiUndo} size={0.55} />
            </TbButton>
            <TbButton onClick={handleRedo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)">
              <Icon path={mdiRedo} size={0.55} />
            </TbButton>

            <Divider />

            {/* Manage Simulation dropdown */}
            <DropdownMenu
              label="Manage Simulation"
              items={manageMenuItems}
            />
            <HiddenFileInput
              ref={importInputRef}
              type="file"
              accept=".xml,.json"
              onChange={handleImport}
            />

            <Divider />
          </>
        )}

        {/* Simulation group (the coolant is chosen on each pump) */}
        <TbButton
          primary
          onClick={handleSimulate}
          disabled={!canSimulate || simulation.loading}
        >
          {simulation.loading ? (
            <SpinIcon path={mdiLoading} size={0.55} />
          ) : (
            <Icon path={mdiPlay} size={0.55} />
          )}
          Simulate
        </TbButton>
        {!isReadOnly && (
          <TbButton
            onClick={handleRecommend}
            disabled={!canSimulate || simulation.recommendLoading}
          >
            {simulation.recommendLoading ? (
              <SpinIcon path={mdiLoading} size={0.55} />
            ) : (
              <Icon path={mdiAutoFix} size={0.55} />
            )}
            Recommend Cooling
          </TbButton>
        )}
        <TbButton
          onClick={handleExportPdf}
          disabled={pdfExporting}
        >
          {pdfExporting ? (
            <SpinIcon path={mdiLoading} size={0.55} />
          ) : (
            <Icon path={mdiFilePdfBox} size={0.55} />
          )}
          PDF
        </TbButton>

        <TopBarSpacer />

        {!isReadOnly && (
          <>
            {agentStatus?.configured && (
              <TbButton
                onClick={() => setShowAgent(v => !v)}
                title="Build and check circuits with the AI assistant"
                primary={showAgent}
              >
                <Icon path={mdiRobotOutline} size={0.55} />
                Assistant
              </TbButton>
            )}
            {/* Misc group */}
            <TbButton onClick={() => setShowTemplatePicker(true)}>
              <Icon path={mdiViewDashboardOutline} size={0.55} />
              Templates
            </TbButton>
            <TbButton onClick={() => setShowRfqModal(true)}>
              <Icon path={mdiEmailOutline} size={0.55} />
              RFQ
            </TbButton>
            <TbButton onClick={() => setShowShareDialog(true)}>
              <Icon path={mdiShareVariantOutline} size={0.55} />
              Share
            </TbButton>
          </>
        )}

        <Divider />

        <UnitToggle
          $active={unitCtx.system === 'imperial'}
          onClick={unitCtx.toggle}
          title={`Switch to ${unitCtx.system === 'metric' ? 'Imperial (psi, GPM, °F)' : 'Metric (kPa, l/min, °C)'}`}
        >
          {unitCtx.system === 'metric' ? 'SI' : 'IMP'}
        </UnitToggle>

        <Divider />

        <UserMenu
          onOpenAuth={openAuthModal}
        />
      </TopBarContainer>

      {/* ── Banners ── */}
      {isReadOnly && (
        <ReadOnlyBanner>
          You are viewing a shared circuit in read-only mode.{' '}
          <BannerLink onClick={() => setShowAuthModal(true)}>Sign in</BannerLink>
          {' '}for full access.
        </ReadOnlyBanner>
      )}
      {auth.user && sharedCircuitContext && (
        <SharedCircuitBanner>
          Viewing shared circuit: "{sharedCircuitContext.title}" &mdash;{' '}
          <SaveBtn onClick={handleSaveSharedCircuit} disabled={savingShared}>
            {savingShared ? 'Saving...' : 'Save to My Circuits'}
          </SaveBtn>
        </SharedCircuitBanner>
      )}

      {/* ── Main 3-panel layout ── */}
      <MainContent>
        {!isReadOnly && (
          <LeftPanel collapsed={!panels.library}>
            {panels.library ? (
              <>
                <PanelHeader>
                  <span>Components</span>
                  <CollapseButton onClick={() => togglePanel('library')}>
                    {'\u25C0'}
                  </CollapseButton>
                </PanelHeader>
                <ElementsLibrary />
              </>
            ) : (
              <ExpandButton onClick={() => togglePanel('library')} title="Expand components panel">
                <Icon path={mdiChevronRight} size={0.7} />
              </ExpandButton>
            )}
          </LeftPanel>
        )}

        <CenterPanel ref={canvasRef}>
          <CanvasArea>
            <FlowCanvas
              schema={schema}
              onSchemaChange={setSchema}
              onNodeSelect={handleNodeSelect}
              onElementSelect={handleElementSelect}
              onMultiSelect={handleMultiSelect}
              selectedElements={selectedElements}
              onControlPointChange={handleControlPointChange}
              activeSimResult={simulation.activeResult}
              addAnnotationRequest={annotationRequest}
              fitViewRequest={fitViewRequest}
              onNodeDoubleClick={handleNodeDoubleClick}
              readOnly={isReadOnly}
            />

            {showRecommendations && simulation.recommendations.length > 0 && (
              <RecommendationTable
                recommendations={simulation.recommendations}
                onSelect={handleRecommendationSelect}
                onClose={() => setShowRecommendations(false)}
                onRequestQuote={() => { setShowRecommendations(false); setShowRfqModal(true); }}
              />
            )}

            {showTemplatePicker && (
              <TemplatePickerModal
                onSelect={handleTemplateSelect}
                onClose={() => setShowTemplatePicker(false)}
              />
            )}

            {showRfqModal && (
              <RfqModal
                schema={schema}
                onClose={() => setShowRfqModal(false)}
              />
            )}

            {showShareDialog && (
              <ShareDialog
                schema={schema}
                onClose={() => setShowShareDialog(false)}
              />
            )}

            {validationErrors.length > 0 && (
              <ValidationErrors
                errors={validationErrors}
                onClose={() => setValidationErrors([])}
              />
            )}

            {showWelcome && (
              <WelcomeScreen
                templates={templates.map(t => ({ id: t.id, name: t.name, description: t.description }))}
                onSelectTemplate={handleWelcomeTemplateSelect}
                onStartBlank={handleWelcomeStartBlank}
                onImport={handleWelcomeImport}
              />
            )}
          </CanvasArea>

          <BottomPanel collapsed={bottomCollapsed} $height={bottomHeight}>
            {!bottomCollapsed && <ResizeHandle onMouseDown={handleBottomResize} />}
            <BottomPanelHeader>
              <BottomTabBar>
                <BottomTab
                  active={bottomTab === 'scenarios'}
                  onClick={() => { setBottomTab('scenarios'); setBottomCollapsed(false); }}
                >
                  Scenarios
                </BottomTab>
                <BottomTab
                  active={bottomTab === 'results'}
                  disabled={simulation.results.length === 0}
                  onClick={() => {
                    if (simulation.results.length > 0) {
                      setBottomTab('results');
                      setBottomCollapsed(false);
                    }
                  }}
                >
                  Results
                </BottomTab>
              </BottomTabBar>
              <BottomPanelToggle onClick={() => setBottomCollapsed(c => !c)}>
                <Icon path={bottomCollapsed ? mdiChevronUp : mdiChevronDown} size={0.7} color="#5f6368" />
              </BottomPanelToggle>
            </BottomPanelHeader>

            {!bottomCollapsed && (
              <BottomPanelContent>
                {bottomTab === 'scenarios' && (
                  <ScenariosPanel
                    scenarios={simParams.scenarios}
                    onChange={handleScenariosChange}
                    nodes={schema.nodes}
                    disabled={simulation.loading || isReadOnly}
                  />
                )}
                {bottomTab === 'results' && simulation.results.length > 0 && (
                  <SimulationResults
                    results={simulation.results}
                    selectedScenarioId={simulation.selectedScenarioId}
                    onSelectScenario={simulation.selectScenario}
                    nodeNames={nodeNames}
                    nodeThresholds={nodeThresholds}
                  />
                )}
              </BottomPanelContent>
            )}

            {thermalWarning && (
              <ThermalWarningBar>
                <Icon path={mdiAlertCircleOutline} size={0.55} color="#e37400" />
                <ThermalWarningText>
                  {thermalWarning.name} exceeds temperature limits in "{thermalWarning.scenario}"
                </ThermalWarningText>
                <CtaBtn onClick={handleRecommend}>
                  <Icon path={mdiAutoFix} size={0.45} />
                  Recommend Cooling
                </CtaBtn>
                <CtaBtn $primary onClick={() => setShowRfqModal(true)}>
                  <Icon path={mdiEmailOutline} size={0.45} />
                  Request Quote
                </CtaBtn>
              </ThermalWarningBar>
            )}
          </BottomPanel>
        </CenterPanel>

        {!isReadOnly && (
          <AgentPanel
            visible={showAgent}
            schema={schema}
            status={agentStatus}
            isLoggedIn={!!auth.user}
            onApply={handleAgentApply}
            onClose={() => setShowAgent(false)}
            onRequestLogin={() => openAuthModal('login')}
          />
        )}

        <RightPanel visible={panels.properties || true}>
          <PanelHeader>
            <span>{selectedAnnotation ? 'Annotation' : 'Properties'}</span>
            {(selectedNode || selectedAnnotation) && (
              <CollapseButton onClick={() => togglePanel('properties')}>
                ✕
              </CollapseButton>
            )}
          </PanelHeader>

          {simulation.error && (
            <ErrorBanner>{simulation.error}</ErrorBanner>
          )}

          {/* Empty state when nothing selected */}
          {!selectedNode && !selectedAnnotation && (
            <RightPanelSection style={{ color: '#9aa0a6', fontSize: 12, textAlign: 'center', padding: '24px 12px' }}>
              Click a component to edit its properties
            </RightPanelSection>
          )}

          {/* Properties panel for selected node */}
          {panels.properties && selectedNode && (
            <RightPanelSection>
              <PropertiesPanel
                node={selectedNode}
                fluids={fluids}
                circuitInfo={selectedCircuitInfo}
                onCircuitCoolantChange={handleCircuitCoolantChange}
                canAddToLibrary={!!auth.user?.is_staff}
                onAddToLibrary={handleAddToLibrary}
                availableModels={availableModels}
                openPickerOnMount={openPickerNodeId === selectedNode.id}
                onPickerOpened={handlePickerOpened}
                readOnly={isReadOnly}
                onNodeUpdate={(nodeId, updates) => {
                  setSchema(prev => {
                    const currentNode = prev.nodes.find(n => n.id === nodeId);
                    const updatedNodes = prev.nodes.map(node =>
                      node.id === nodeId
                        ? { ...node, data: { ...node.data, ...updates } }
                        : node
                    );

                    // Clean up edges when port count decreases
                    let updatedEdges = prev.edges;
                    if (currentNode && updates.component) {
                      const oldInputPorts = currentNode.data.component.input_ports;
                      const oldOutputPorts = currentNode.data.component.output_ports;
                      const newInputPorts = updates.component.input_ports ?? oldInputPorts;
                      const newOutputPorts = updates.component.output_ports ?? oldOutputPorts;

                      if (newInputPorts < oldInputPorts || newOutputPorts < oldOutputPorts) {
                        updatedEdges = prev.edges.filter(edge => {
                          // Remove edges connected to input handles that no longer exist
                          if (edge.target === nodeId && edge.targetHandle) {
                            const portIndex = parseInt(edge.targetHandle.replace('input-', ''), 10);
                            if (!isNaN(portIndex) && portIndex >= newInputPorts) return false;
                          }
                          // Remove edges connected to output handles that no longer exist
                          if (edge.source === nodeId && edge.sourceHandle) {
                            const portIndex = parseInt(edge.sourceHandle.replace('output-', ''), 10);
                            if (!isNaN(portIndex) && portIndex >= newOutputPorts) return false;
                          }
                          return true;
                        });
                      }
                    }

                    return { ...prev, nodes: updatedNodes, edges: updatedEdges };
                  });
                }}
              />
            </RightPanelSection>
          )}

          {/* Annotation color picker for selected annotation */}
          {panels.properties && selectedAnnotation && (
            <RightPanelSection>
              <AnnotationSectionLabel>Color</AnnotationSectionLabel>
              <ColorSwatchGrid>
                {ANNOTATION_COLORS.map(c => (
                  <ColorSwatch
                    key={c.value}
                    $color={c.value}
                    $active={(selectedAnnotation.data as any).color === c.value}
                    title={c.label}
                    onClick={() => handleAnnotationColorChange(c.value)}
                  />
                ))}
              </ColorSwatchGrid>
            </RightPanelSection>
          )}
        </RightPanel>
      </MainContent>

      {/* Circuit Browser */}
      {showCircuitBrowser && (
        <CircuitBrowser
          onClose={() => setShowCircuitBrowser(false)}
          onLoadCircuit={(loadedSchema, title) => {
            setSchema(loadedSchema);
          }}
          currentSchema={schema}
        />
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          initialMode={authModalMode}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </AppContainer>
    </FluidNamesContext.Provider>
    </UnitContext.Provider>
    </AuthContext.Provider>
  );
};

export default App;
